import type { FullShipment } from '@fv/client-types'
import type { Workflow } from '@fv/models'

import { useAccountFeatures, useRole } from '@/features/auth/hooks'
import { useUserSettings } from '@/hooks/settings'

import { hasRouteGuide } from './funcs'
import { useBookTpeRate, useSkipToSpot } from './mutations'
import { useRouteGuideExecution } from './queries'

type UnifiedFlow = {
  isEnabled: (workflow?: Workflow) => boolean
  isLoading: boolean
}

/**
 * Waterfall accounts award truckload through the route guide rather than the
 * spot quote board, so their truckload shipments get the same streamlined
 * create / rate / book pages as vendors. LTL and parcel keep the quote pages.
 * Callers that redirect on this must hold until `isLoading` clears.
 */
export const useUnifiedShipmentFlow = (): UnifiedFlow => {
  const role = useRole()
  const { truckLoad } = useAccountFeatures()
  const settingsQuery = useUserSettings()

  if (role === 'vendor') return { isEnabled: () => true, isLoading: false }

  const isWaterfall =
    truckLoad && !!settingsQuery.data?.settings?.routeGuide?.isEnabled

  return {
    isEnabled: workflow => isWaterfall && workflow === 'truckload',
    isLoading: truckLoad && !settingsQuery.isFetched,
  }
}

export const useShowRouteGuide = (load?: FullShipment) => {
  const { truckLoad } = useAccountFeatures()
  return truckLoad && hasRouteGuide(load)
}

export const useRouteGuideView = (load?: FullShipment) => {
  const loadId = load?.loadId
  const executionQuery = useRouteGuideExecution(loadId, {
    enabled: hasRouteGuide(load),
  })
  const skipToSpot = useSkipToSpot(loadId ?? '')
  const bookTpeRate = useBookTpeRate(loadId ?? '')
  const execution = executionQuery.data

  return {
    execution,
    skipToSpot,
    bookTpeRate,
    tpeRate: execution?.tpeRate ?? null,
    canSkip: execution?.status === 'running',
    isLoading: executionQuery.isLoading,
    isError: executionQuery.isError,
  }
}

export type RouteGuideView = ReturnType<typeof useRouteGuideView>
